import Select from 'react-select';
import styled from 'styled-components';
import type {
    ControlProps,
    Props as ReactSelectProps,
    StylesConfig,
    ValueType,
} from 'react-select';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { borders, colors, spaces, typography } from '@99designs/emu/settings';
import { IconDown1 } from '@99designs/icons';

const DownIcon = styled(IconDown1)`
    &:hover {
        color: ${colors.grey700};
    }
`;

const DropdownIndicator = () => (
    <Box paddingRight={['1bu', '2bu', '2bu']}>
        <DownIcon width="2em" height="2em" />
    </Box>
);

export interface Option {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    options: Option[];
    onChange: (value: ValueType<Option, boolean>) => void;
    label?: string;
    value?: ValueType<Option, boolean>;
    name?: string;
    placeholderText: string;
}

const reactSelectStyleAndTheme: {
    styles: StylesConfig<Option, boolean>;
    theme: NonNullable<ReactSelectProps['theme']>;
} = {
    styles: {
        valueContainer: (provided) => ({
            ...provided,
            padding: spaces['2bu'],
            fontSize: `${typography.fontSizes.paragraphs.jumbo}px`,
            [mediaQuery.only('small')]: {
                padding: spaces['1bu'],
                fontSize: `${typography.fontSizes.paragraphs.large}px`,
            },
        }),
        control: (provided, state: ControlProps<Option, boolean>) => ({
            ...provided,
            borderRadius: `${borders.borderRadiuses.small}px`,
            border: `1px solid ${colors.grey500}`,
            color: colors.grey800,
            boxShadow: 'none',
            outline: 'none',
            '&:hover': {
                borderColor: colors.grey700,
            },
            ...(state.isFocused && {
                borderColor: colors.grey700,
            }),
        }),
        menuList: (provided) => ({
            ...provided,
            [mediaQuery.only('small')]: {
                maxHeight: '200px', // adjusted to not get hidden by keyboard on mobile
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: colors.grey800,
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            display: 'none',
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: colors.grey800,
            [mediaQuery.minimum('medium')]: {
                marginRight: spaces['0.5bu'],
            },
            svg: {
                height: '2em',
                width: '2em',
            },
            '&:hover': {
                color: colors.grey700,
            },
        }),
        option: (provided) => ({
            ...provided,
            color: colors.grey800,
        }),
        placeholder: (provided) => ({
            ...provided,
            color: colors.grey700,
        }),
    },
    theme: (theme) => ({
        ...theme,
        colors: {
            ...theme.colors,
            primary25: colors.grey100,
            primary: colors.grey100,
        },
    }),
};

export function SearchableSelect({
    label,
    name,
    onChange,
    options,
    value,
    placeholderText,
}: SearchableSelectProps) {
    return (
        <Select<Option, boolean>
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            options={options}
            classNamePrefix={'select'}
            components={{
                DropdownIndicator,
                Placeholder: () => <span>{placeholderText}</span>,
            }}
            blurInputOnSelect
            isClearable
            {...reactSelectStyleAndTheme}
        />
    );
}
